/**
 * @fileoverview Where Stripe sends the gardener after paying.
 *
 * Tells the backend which checkout just finished, so the app knows before
 * the webhook says so, then says thank you and points back at the garden.
 *
 * Where: the route /subscribe/success, Stripe Checkout's success_url
 * (?session_id=).
 * API: POST /billing/sync { sessionId } (syncCheckout), then the billing
 * status is read again.
 * Words (en/es): billing.success.*.
 * Watch out: a checkout Stripe has not finished reads as "Nearly there"
 * (billing.success.unsure), not as a failure; the webhook catches up.
 * A check that failed on our side (a 500, no connection; D31,
 * lib/serverFailure) still says "Nearly there" (a payment made will show),
 * and under it the standard sentence, reported to Sentry, with the support
 * address as a link.
 *
 * @module SubscribeSuccess
 */

import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { isServerFailure } from '@/shared/lib/serverFailure';
import { ServerFailureNote } from '@/shared/components/ServerFailure';
import { syncCheckout, type Access } from '@/shared/api/billing';
import { trackEvent } from '@/shared/lib/analytics';
import { cn } from '@/lib/utils';
import { BILLING_QUERY_KEY } from './lib/useBilling';

/** The page Stripe returns to: checks the checkout, then thanks the gardener or says it is not sure yet. */
export const SubscribeSuccess: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const sessionId = params.get('session_id');
  const [access, setAccess] = React.useState<Access | null>(null);
  const [failed, setFailed] = React.useState(false);
  // What the check threw, when it failed: on our side, the standard sentence is said too.
  const [failure, setFailure] = React.useState<unknown>(null);

  React.useEffect(() => {
    if (!sessionId) { setFailed(true); return; }
    let cancelled = false;
    syncCheckout(sessionId)
      .then((result) => {
        if (cancelled) return;
        setAccess(result);
        // Once per checkout: a reload of this page is not a second subscription.
        if (result.active) {
          const seen = `grow.subscribedCounted.${sessionId}`;
          try {
            if (window.sessionStorage.getItem(seen) !== '1') {
              window.sessionStorage.setItem(seen, '1');
              trackEvent('subscribed');
            }
          } catch { trackEvent('subscribed'); }
        }
      })
      .catch((error) => { if (!cancelled) { setFailure(error); setFailed(true); } })
      .finally(() => { void queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY }); });
    return () => { cancelled = true; };
  }, [sessionId, queryClient]);

  const heading = 'font-semibold font-body';

  return (
    <div className="mx-auto max-w-lg p-4 text-center">
      {failed ? (
        <>
          <h1 className={cn(heading, 'mt-8 text-2xl')}>{t('billing.success.unsureTitle')}</h1>
          <p className="mt-2 text-muted-foreground">{t('billing.success.unsure')}</p>
          {isServerFailure(failure) && <ServerFailureNote error={failure} where="Subscribe success" className="mt-2 text-muted-foreground" />}
        </>
      ) : !access ? (
        <div className="flex items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
          <Spinner size={20} />{t('billing.success.checking')}
        </div>
      ) : (
        <>
          <h1 className={cn(heading, 'mt-8 text-2xl')}>{t('billing.success.title')}</h1>
          <p className="mt-2 text-muted-foreground">
            {access.active ? t('billing.success.body') : t('billing.success.unsure')}
          </p>
        </>
      )}
      <Button asChild className="mt-6">
        <Link to="/plants">{t('billing.success.toGarden')}</Link>
      </Button>
    </div>
  );
};

export default SubscribeSuccess;
